import {
  pickAdminDashboardLocaleText,
  type AdminDashboardLocaleText,
} from "./admin-dashboard-copy";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";

export type AdminOperatorExplanationCategory =
  | "status"
  | "source"
  | "concept"
  | "route";

export type AdminOperatorExplanationPlacement =
  | "section_summary"
  | "metric_caption"
  | "detail_help_block"
  | "empty_error_guidance";

export type AdminOperatorExplanationScreen =
  | "overview"
  | "users_summary"
  | "users_detail"
  | "failed_flows"
  | "device_kpis"
  | "jobs"
  | "releases";

export type AdminOperatorExplanationSlot =
  | "overview_top_summary"
  | "overview_table_reading_order"
  | "users_summary_top_summary"
  | "users_summary_table_reading_order"
  | "users_summary_scenario_timeline"
  | "users_summary_current_evidence"
  | "users_detail_top_summary"
  | "users_detail_risk_summary"
  | "users_detail_daily_triage"
  | "users_detail_media_reading_flow"
  | "users_detail_media_workspace"
  | "users_detail_media_coverage"
  | "users_detail_source_surface"
  | "users_detail_missing_sources"
  | "users_detail_incident_workspace"
  | "failed_flows_top_summary"
  | "failed_flows_table_reading_order"
  | "failed_flows_detail_request_evidence"
  | "device_kpis_top_summary"
  | "device_kpis_table_reading_order"
  | "device_kpis_preset_semantics"
  | "jobs_top_hotspot"
  | "jobs_table_reading_order"
  | "releases_top_hotspot"
  | "releases_table_reading_order"
  | "shared_empty_state_guidance"
  | "shared_error_state_guidance";

export type AdminOperatorExplanationKey =
  | "status_fresh"
  | "status_stale"
  | "status_partial"
  | "status_unavailable"
  | "status_failed"
  | "status_stalled"
  | "source_request_outcome"
  | "source_client_flow_event"
  | "source_projection"
  | "source_job"
  | "concept_latest_save"
  | "concept_latest_failure"
  | "concept_current_evidence"
  | "concept_scenario_timeline"
  | "concept_risk_summary"
  | "concept_release_regression"
  | "concept_same_install_bucket"
  | "concept_freshness_mismatch"
  | "concept_query_snapshot"
  | "concept_source_freshness"
  | "concept_scenario_preset"
  | "concept_representative_photo"
  | "concept_photo_corpus_reading_flow"
  | "concept_photo_compare_parity"
  | "concept_binary_review_path"
  | "concept_selected_artifact_workspace"
  | "concept_media_coverage_ledger"
  | "concept_same_device_log_artifact"
  | "concept_engineering_handoff_package"
  | "route_incident_route";

export type AdminOperatorExplanationDefinition = {
  category: AdminOperatorExplanationCategory;
  label: AdminDashboardLocaleText<string>;
  shortDescription: AdminDashboardLocaleText<string>;
  allowedPlacements: readonly AdminOperatorExplanationPlacement[];
};

export type AdminOperatorExplanationPlacementRule = {
  screen: AdminOperatorExplanationScreen;
  slot: AdminOperatorExplanationSlot;
  placement: AdminOperatorExplanationPlacement;
  explanationKeys: readonly AdminOperatorExplanationKey[];
};

export type AdminOperatorExplanationEntry = {
  key: AdminOperatorExplanationKey;
  category: AdminOperatorExplanationCategory;
  label: string;
  description: string;
};

export type AdminResolvedOperatorExplanationSlot = {
  placement: AdminOperatorExplanationPlacement;
  entries: AdminOperatorExplanationEntry[];
};

export const adminOperatorExplanationDefinitions = {
  status_fresh: {
    category: "status",
    label: { en: "Fresh", ko: "준비" },
    shortDescription: {
      en: "The latest source is current enough for direct operator judgment.",
      ko: "최신 소스가 현재 운영 판단에 바로 사용할 만큼 최신 상태입니다.",
    },
    allowedPlacements: ["metric_caption", "detail_help_block"],
  },
  status_stale: {
    category: "status",
    label: { en: "Stale", ko: "지연" },
    shortDescription: {
      en: "This source is older than the expected refresh window and may hide newer incidents.",
      ko: "이 소스는 기대한 새로고침 창보다 오래되어 더 최신 incident를 놓칠 수 있습니다.",
    },
    allowedPlacements: ["metric_caption", "detail_help_block", "empty_error_guidance"],
  },
  status_partial: {
    category: "status",
    label: { en: "Partial", ko: "부분" },
    shortDescription: {
      en: "Only part of the expected source set is ready, so compare related screens before concluding.",
      ko: "기대한 source 집합의 일부만 준비되어 있으므로 결론 전에 연관 화면을 함께 비교해야 합니다.",
    },
    allowedPlacements: ["metric_caption", "detail_help_block", "empty_error_guidance"],
  },
  status_unavailable: {
    category: "status",
    label: { en: "Unavailable", ko: "미지원" },
    shortDescription: {
      en: "This source or explanation is not currently available for the selected row or route.",
      ko: "선택한 행 또는 화면에서는 이 source 또는 설명을 현재 사용할 수 없습니다.",
    },
    allowedPlacements: ["detail_help_block", "empty_error_guidance"],
  },
  status_failed: {
    category: "status",
    label: { en: "Failed", ko: "실패" },
    shortDescription: {
      en: "The tracked flow reached a terminal error and needs direct investigation.",
      ko: "추적 중인 흐름이 최종 오류에 도달해 직접 조사가 필요합니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block"],
  },
  status_stalled: {
    category: "status",
    label: { en: "Stalled", ko: "정체" },
    shortDescription: {
      en: "The flow did not finish cleanly and needs surrounding request, job, or release context.",
      ko: "흐름이 정상 종료되지 않았으므로 주변 request, job, release 문맥을 함께 봐야 합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  source_request_outcome: {
    category: "source",
    label: { en: "Request outcome", ko: "요청 결과" },
    shortDescription: {
      en: "Server-side request outcome rows preserve requestId, errorCode, useCase, release, and device linkage.",
      ko: "서버 측 request outcome 행은 requestId, errorCode, useCase, release, device 연결을 보존합니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block"],
  },
  source_client_flow_event: {
    category: "source",
    label: { en: "Client flow event", ko: "클라이언트 흐름 이벤트" },
    shortDescription: {
      en: "Client flow events capture bounded in-app steps that do not always create a server request.",
      ko: "클라이언트 흐름 이벤트는 항상 서버 요청을 만들지 않는 bounded 인앱 단계를 수집합니다.",
    },
    allowedPlacements: ["metric_caption", "detail_help_block"],
  },
  source_projection: {
    category: "source",
    label: { en: "Projection", ko: "프로젝션" },
    shortDescription: {
      en: "Projection tables aggregate operator-facing read models from request, job, and flow sources.",
      ko: "프로젝션 테이블은 request, job, flow source를 운영자용 read model로 집계합니다.",
    },
    allowedPlacements: ["metric_caption", "detail_help_block", "empty_error_guidance"],
  },
  source_job: {
    category: "source",
    label: { en: "Job", ko: "작업" },
    shortDescription: {
      en: "Job rows explain queued, processing, and failed async work that continues after the initial request.",
      ko: "작업 행은 최초 요청 뒤에도 이어지는 대기, 처리 중, 실패 비동기 작업을 설명합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_latest_save: {
    category: "concept",
    label: { en: "Latest save", ko: "최근 저장" },
    shortDescription: {
      en: "The most recent successful save proves the last known good state before or after a failure.",
      ko: "가장 최근의 성공 저장은 실패 전후의 마지막 정상 상태를 보여줍니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_latest_failure: {
    category: "concept",
    label: { en: "Latest failure", ko: "최근 실패" },
    shortDescription: {
      en: "The newest linked failure is the primary starting point for request, job, and release drill-down.",
      ko: "가장 최근에 연결된 실패는 request, job, release 드릴다운의 1차 시작점입니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block", "empty_error_guidance"],
  },
  concept_current_evidence: {
    category: "concept",
    label: { en: "Current evidence", ko: "현재 증거 묶음" },
    shortDescription: {
      en: "Current evidence keeps the latest failure, latest save, and canonical risk summary together so operators start from one verified bundle before drilling into detail, failed flow, or release comparison.",
      ko: "현재 증거 묶음은 최근 실패, 최근 저장, 정규 위험 요약을 함께 유지해 운영자가 상세, 오류 흐름, 릴리스 비교로 내려가기 전에 검증된 기준 묶음 하나에서 출발하게 합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_scenario_timeline: {
    category: "concept",
    label: { en: "Scenario timeline", ko: "시나리오 타임라인" },
    shortDescription: {
      en: "The user summary timeline keeps auth, pet, social, chat, community, daily, health, receipt, medication, ride, files, and account deletion in one reading order so operators compare the same user across scenarios instead of jumping between ad-hoc lists.",
      ko: "사용자 요약 타임라인은 인증, 펫, 소셜, 채팅, 커뮤니티, 일상, 건강, 영수증, 복약, 이동지원, 파일, 계정 삭제를 하나의 읽기 순서에 유지해 운영자가 임의 목록을 오가기보다 같은 사용자를 시나리오별로 비교하게 합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_risk_summary: {
    category: "concept",
    label: { en: "Risk summary", ko: "위험 요약" },
    shortDescription: {
      en: "A compact operator signal that ranks whether a user or release needs attention now.",
      ko: "사용자나 릴리스가 지금 주의를 요하는지 우선순위를 매기는 압축 운영 신호입니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block"],
  },
  concept_release_regression: {
    category: "concept",
    label: { en: "Release regression", ko: "릴리스 회귀" },
    shortDescription: {
      en: "Compare the same release or appVersion bucket to decide whether the issue is isolated or broad.",
      ko: "같은 release 또는 appVersion 버킷을 비교해 이슈가 국소적인지 더 넓은 회귀인지 판단합니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block"],
  },
  concept_same_install_bucket: {
    category: "concept",
    label: { en: "Same install bucket", ko: "동일 설치본 버킷" },
    shortDescription: {
      en: "This bucket keeps one deviceInstallId with its release, appVersion, and platform context together.",
      ko: "이 버킷은 하나의 deviceInstallId를 release, appVersion, platform 문맥과 함께 유지합니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block"],
  },
  concept_freshness_mismatch: {
    category: "concept",
    label: { en: "Freshness mismatch", ko: "신선도 불일치" },
    shortDescription: {
      en: "A newer source event is already visible, but the KPI baseline still lags behind, so operators must compare source freshness with the projection snapshot before concluding that no new incident exists.",
      ko: "더 최신 source 이벤트가 이미 보이는데 KPI 기준선은 아직 뒤처져 있으므로, 새 incident가 없다고 판단하기 전에 source 신선도와 projection 조회 스냅샷을 함께 비교해야 합니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block", "empty_error_guidance"],
  },
  concept_query_snapshot: {
    category: "concept",
    label: { en: "Query snapshot", ko: "조회 스냅샷" },
    shortDescription: {
      en: "A query snapshot records when the current rows were generated and which request produced them.",
      ko: "조회 스냅샷은 현재 행이 언제 생성됐고 어떤 request가 만들었는지 기록합니다.",
    },
    allowedPlacements: ["metric_caption", "detail_help_block"],
  },
  concept_source_freshness: {
    category: "concept",
    label: { en: "Source freshness", ko: "소스 신선도" },
    shortDescription: {
      en: "Freshness shows whether the operator is reading current source data or delayed projection output.",
      ko: "신선도는 운영자가 최신 source 데이터를 보는지 지연된 projection 출력을 보는지 알려줍니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block", "empty_error_guidance"],
  },
  concept_scenario_preset: {
    category: "concept",
    label: { en: "Scenario preset", ko: "시나리오 프리셋" },
    shortDescription: {
      en: "A scenario preset groups the same install or user context into one operator reading path.",
      ko: "시나리오 프리셋은 같은 설치본 또는 사용자 문맥을 하나의 운영자 reading path로 묶습니다.",
    },
    allowedPlacements: ["section_summary", "metric_caption", "detail_help_block"],
  },
  concept_representative_photo: {
    category: "concept",
    label: { en: "Representative photo", ko: "대표 사진" },
    shortDescription: {
      en: "The representative photo is the first triage entry, but it still belongs to the same uploaded corpus and must reconnect to the same artifact workspace.",
      ko: "대표 사진은 첫 조사 진입점이지만, 같은 업로드 코퍼스에 속하므로 동일 artifact 작업면으로 다시 이어져야 합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_photo_corpus_reading_flow: {
    category: "concept",
    label: { en: "Photo corpus reading flow", ko: "사진 코퍼스 reading flow" },
    shortDescription: {
      en: "Read the representative photo first, then narrow by scenario pivot before pinning one corpus row for preview, download, and trace comparison.",
      ko: "대표 사진을 먼저 읽고 scenario pivot으로 좁힌 뒤, 미리보기, 다운로드, 추적 비교를 위해 코퍼스 row 하나를 고정합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_photo_compare_parity: {
    category: "concept",
    label: { en: "Photo compare parity", ko: "사진 비교 parity" },
    shortDescription: {
      en: "Representative photo and selected corpus row must use the same compare-ready, original-download issue, delivery source, and next-action glossary.",
      ko: "대표 사진과 선택 코퍼스 row는 같은 compare-ready, 원본 다운로드 이슈, 전달 source, 다음 액션 glossary를 함께 사용해야 합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_binary_review_path: {
    category: "concept",
    label: { en: "Binary review path", ko: "binary review path" },
    shortDescription: {
      en: "The binary review path tells the operator whether to start from preview, original download, or fallback and trace before treating the artifact as missing.",
      ko: "binary review path는 artifact를 누락으로 단정하기 전에 미리보기, 원본 다운로드, 또는 fallback과 추적 중 어디서 조사를 시작할지 알려줍니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_selected_artifact_workspace: {
    category: "concept",
    label: { en: "Selected artifact workspace", ko: "선택 artifact 작업면" },
    shortDescription: {
      en: "Pin one corpus row, then compare preview support, original download, fallback, and linked request trace without losing the same artifact identity.",
      ko: "코퍼스 row 하나를 고정한 뒤, 같은 artifact identity를 유지한 채 미리보기 지원, 원본 다운로드, fallback, 연결된 요청 추적을 함께 비교합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_media_coverage_ledger: {
    category: "concept",
    label: { en: "Coverage ledger", ko: "coverage ledger" },
    shortDescription: {
      en: "The coverage ledger summarizes which scenarios are previewable, download-only, metadata-only, missing, or still blocked by rollout gaps.",
      ko: "coverage ledger는 어떤 시나리오가 미리보기 가능, 다운로드 전용, 메타데이터만 가능, 누락, 또는 rollout 공백으로 막혀 있는지 요약합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_same_device_log_artifact: {
    category: "concept",
    label: { en: "Same-device log artifact", ko: "same-device 로그 artifact" },
    shortDescription: {
      en: "A bounded device-local incident bundle that stays tied to the failing install, release, and request trace instead of falling back to server logs only.",
      ko: "서버 로그만으로 대체하지 않고 실패한 설치본, 릴리스, 요청 추적에 계속 묶여 있는 bounded device-local incident bundle입니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  concept_engineering_handoff_package: {
    category: "concept",
    label: { en: "Engineering handoff package", ko: "엔지니어링 handoff 패키지" },
    shortDescription: {
      en: "One operator-facing block that keeps photo evidence, same-device log readiness, and trace linkage together before the incident is handed to engineering.",
      ko: "incident를 엔지니어링으로 넘기기 전에 사진 증거, same-device 로그 readiness, 추적 연결을 하나의 운영자용 블록에 함께 유지하는 구조입니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block"],
  },
  route_incident_route: {
    category: "route",
    label: { en: "Incident route", ko: "사고 조사 경로" },
    shortDescription: {
      en: "The incident route defines the required operator reading order: request log, user, release, and job fallback.",
      ko: "사고 조사 경로는 request log, user, release, job fallback 순서의 필수 운영자 reading order를 정의합니다.",
    },
    allowedPlacements: ["section_summary", "detail_help_block", "empty_error_guidance"],
  },
} satisfies Record<AdminOperatorExplanationKey, AdminOperatorExplanationDefinition>;

export const adminOperatorExplanationPlacementRules = [
  {
    screen: "overview",
    slot: "overview_top_summary",
    placement: "section_summary",
    explanationKeys: ["concept_risk_summary", "concept_release_regression", "concept_source_freshness"],
  },
  {
    screen: "overview",
    slot: "overview_table_reading_order",
    placement: "detail_help_block",
    explanationKeys: ["source_request_outcome", "concept_latest_failure", "concept_release_regression"],
  },
  {
    screen: "users_summary",
    slot: "users_summary_top_summary",
    placement: "section_summary",
    explanationKeys: ["concept_latest_failure", "concept_latest_save", "concept_risk_summary"],
  },
  {
    screen: "users_summary",
    slot: "users_summary_table_reading_order",
    placement: "detail_help_block",
    explanationKeys: ["concept_current_evidence", "concept_latest_failure", "concept_latest_save", "concept_risk_summary"],
  },
  {
    screen: "users_summary",
    slot: "users_summary_scenario_timeline",
    placement: "detail_help_block",
    explanationKeys: ["concept_scenario_timeline", "concept_latest_failure", "concept_latest_save", "concept_risk_summary"],
  },
  {
    screen: "users_summary",
    slot: "users_summary_current_evidence",
    placement: "detail_help_block",
    explanationKeys: ["concept_current_evidence", "concept_latest_failure", "concept_latest_save", "concept_risk_summary"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_top_summary",
    placement: "detail_help_block",
    explanationKeys: ["concept_current_evidence", "concept_latest_failure", "concept_latest_save", "concept_risk_summary"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_risk_summary",
    placement: "detail_help_block",
    explanationKeys: ["concept_risk_summary", "concept_latest_failure"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_daily_triage",
    placement: "detail_help_block",
    explanationKeys: ["concept_latest_failure", "concept_latest_save", "concept_risk_summary"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_media_reading_flow",
    placement: "detail_help_block",
    explanationKeys: [
      "concept_representative_photo",
      "concept_photo_corpus_reading_flow",
      "concept_photo_compare_parity",
      "concept_binary_review_path",
    ],
  },
  {
    screen: "users_detail",
    slot: "users_detail_media_workspace",
    placement: "detail_help_block",
    explanationKeys: [
      "concept_representative_photo",
      "concept_selected_artifact_workspace",
      "concept_photo_compare_parity",
      "concept_binary_review_path",
      "source_request_outcome",
    ],
  },
  {
    screen: "users_detail",
    slot: "users_detail_media_coverage",
    placement: "detail_help_block",
    explanationKeys: ["concept_media_coverage_ledger", "concept_source_freshness", "status_partial"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_source_surface",
    placement: "detail_help_block",
    explanationKeys: ["concept_binary_review_path", "concept_source_freshness", "source_projection"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_missing_sources",
    placement: "detail_help_block",
    explanationKeys: ["status_partial", "concept_source_freshness", "source_projection"],
  },
  {
    screen: "users_detail",
    slot: "users_detail_incident_workspace",
    placement: "detail_help_block",
    explanationKeys: [
      "route_incident_route",
      "concept_selected_artifact_workspace",
      "concept_same_device_log_artifact",
      "concept_engineering_handoff_package",
    ],
  },
  {
    screen: "failed_flows",
    slot: "failed_flows_top_summary",
    placement: "section_summary",
    explanationKeys: ["route_incident_route", "concept_latest_failure", "concept_release_regression"],
  },
  {
    screen: "failed_flows",
    slot: "failed_flows_table_reading_order",
    placement: "detail_help_block",
    explanationKeys: ["route_incident_route", "source_request_outcome", "concept_source_freshness"],
  },
  {
    screen: "failed_flows",
    slot: "failed_flows_detail_request_evidence",
    placement: "detail_help_block",
    explanationKeys: ["route_incident_route", "source_request_outcome", "source_job"],
  },
  {
    screen: "device_kpis",
    slot: "device_kpis_top_summary",
    placement: "section_summary",
    explanationKeys: [
      "concept_same_install_bucket",
      "concept_freshness_mismatch",
      "concept_query_snapshot",
      "concept_source_freshness",
    ],
  },
  {
    screen: "device_kpis",
    slot: "device_kpis_table_reading_order",
    placement: "detail_help_block",
    explanationKeys: ["concept_same_install_bucket", "source_request_outcome", "concept_source_freshness"],
  },
  {
    screen: "device_kpis",
    slot: "device_kpis_preset_semantics",
    placement: "metric_caption",
    explanationKeys: ["concept_scenario_preset", "source_request_outcome", "source_client_flow_event", "source_projection"],
  },
  {
    screen: "jobs",
    slot: "jobs_top_hotspot",
    placement: "section_summary",
    explanationKeys: ["status_failed", "source_job", "concept_latest_failure"],
  },
  {
    screen: "jobs",
    slot: "jobs_table_reading_order",
    placement: "detail_help_block",
    explanationKeys: ["source_job", "concept_latest_failure", "concept_query_snapshot"],
  },
  {
    screen: "releases",
    slot: "releases_top_hotspot",
    placement: "section_summary",
    explanationKeys: ["concept_release_regression", "concept_source_freshness", "concept_latest_failure"],
  },
  {
    screen: "releases",
    slot: "releases_table_reading_order",
    placement: "detail_help_block",
    explanationKeys: ["concept_release_regression", "concept_source_freshness", "concept_latest_failure"],
  },
  {
    screen: "overview",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_query_snapshot", "concept_source_freshness"],
  },
  {
    screen: "overview",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["status_unavailable", "concept_source_freshness", "concept_query_snapshot"],
  },
  {
    screen: "users_summary",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_latest_failure", "concept_query_snapshot"],
  },
  {
    screen: "users_summary",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_latest_failure", "concept_query_snapshot", "concept_risk_summary"],
  },
  {
    screen: "users_detail",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_latest_save", "concept_query_snapshot"],
  },
  {
    screen: "users_detail",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_risk_summary", "concept_latest_failure", "concept_query_snapshot"],
  },
  {
    screen: "failed_flows",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["route_incident_route", "concept_query_snapshot"],
  },
  {
    screen: "failed_flows",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["route_incident_route", "concept_source_freshness"],
  },
  {
    screen: "device_kpis",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_same_install_bucket", "concept_query_snapshot", "concept_source_freshness"],
  },
  {
    screen: "device_kpis",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["status_unavailable", "concept_scenario_preset", "concept_source_freshness"],
  },
  {
    screen: "jobs",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["source_job", "concept_query_snapshot"],
  },
  {
    screen: "jobs",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["status_unavailable", "source_job", "concept_latest_failure"],
  },
  {
    screen: "releases",
    slot: "shared_empty_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["concept_release_regression", "concept_query_snapshot"],
  },
  {
    screen: "releases",
    slot: "shared_error_state_guidance",
    placement: "empty_error_guidance",
    explanationKeys: ["status_unavailable", "concept_release_regression", "concept_source_freshness"],
  },
] satisfies readonly AdminOperatorExplanationPlacementRule[];

export function formatAdminOperatorExplanationLabel(
  locale: AdminDashboardLocale,
  key: AdminOperatorExplanationKey,
): string {
  return pickAdminDashboardLocaleText(locale, adminOperatorExplanationDefinitions[key].label);
}

export function formatAdminOperatorExplanationDescription(
  locale: AdminDashboardLocale,
  key: AdminOperatorExplanationKey,
): string {
  return pickAdminDashboardLocaleText(locale, adminOperatorExplanationDefinitions[key].shortDescription);
}

export function listAdminOperatorExplanationKeys(): AdminOperatorExplanationKey[] {
  return Object.keys(adminOperatorExplanationDefinitions) as AdminOperatorExplanationKey[];
}

export function findAdminOperatorExplanationPlacementRule(
  screen: AdminOperatorExplanationScreen,
  slot: AdminOperatorExplanationSlot,
): AdminOperatorExplanationPlacementRule | undefined {
  return adminOperatorExplanationPlacementRules.find((rule) => rule.screen === screen && rule.slot === slot);
}

export function resolveAdminOperatorExplanationSlot(
  locale: AdminDashboardLocale,
  screen: AdminOperatorExplanationScreen,
  slot: AdminOperatorExplanationSlot,
): AdminResolvedOperatorExplanationSlot | undefined {
  const rule = findAdminOperatorExplanationPlacementRule(screen, slot);
  if (!rule) {
    return undefined;
  }

  return {
    placement: rule.placement,
    entries: rule.explanationKeys.map((key) => ({
      key,
      category: adminOperatorExplanationDefinitions[key].category,
      label: formatAdminOperatorExplanationLabel(locale, key),
      description: formatAdminOperatorExplanationDescription(locale, key),
    })),
  };
}
